import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch } from 'app/hooks';
import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmTabState } from 'common/hooks/usePalmTabState';
import { fetchRoleClaimMemberships } from 'common/services/api/RoleClaimMembership/RoleClaimMembership.thunk';
import { ActionDefinition, ColumnDefinition, ColumnType, Operations } from 'common/types/TypeDefs';
import { claimTypeDropdownConfiguration } from 'common/utils/ClaimTypeDropdownFilterConfiguration';

import { RoleClaimMembershipCreateContent } from './RoleClaimMembershipCreateDrawerContent';
import { RoleClaimMembershipDeleteDialogContent } from './roleClaimMembershipDeleteDialogContent';
import {
    addNewFilter,
    checkClaimTypeFilter,
    clearFilters,
    removeFilter,
    selectFilters,
} from './roleClaimMembershipSlice';

export function RoleClaimMembershipTab() {
    const typeName = 'Claim';
    const { suiteId = '', tenantId = '', roleId = '' } = useParams();
    const dispatch = useAppDispatch();

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager ]);
    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.MultiTenantRO, Roles.UserManager, Roles.AppManager ]);

    const dropdownProps = [{
        dropdownInfo: claimTypeDropdownConfiguration,
        dropdownReducer: (label: string) => dispatch(checkClaimTypeFilter(label))
    }];

    const { processing, sortModel, setSortModel, filterProp, fetchEntities } = usePalmTabState({
        typeName,
        fetchParams: { tenantId, roleId },
        fetchThunk: fetchRoleClaimMemberships,
        filterActions: { selectFilters, addNewFilter, removeFilter, clearFilters },
        dropdownProps,
    });

    const onGetRowLink = useCallback((claimId: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Claims}/${claimId}/${Routes.Roles}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: onGetRowLink },
        { action: Operations.Edit, disabled: shouldDisableButtons },
        { action: Operations.Delete, disabled: shouldDisableButtons }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'claimId', columnName: 'Claim ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'name', columnName: 'Claim Name', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'claimDataTypeName', columnName: 'Claim Type', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'claimValue', columnName: 'Claim Value', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        title={typeName}
        deleteFunction={RoleClaimMembershipDeleteDialogContent}
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        contentFunction={RoleClaimMembershipCreateContent}
        isCreateButtonDisabled={shouldDisableButtons}
        filter={filterProp}
        operationName={Operations.Link}
        getRowLink={shouldDisableOpenButton ? undefined : onGetRowLink}
        refreshDataGridClick={() => fetchEntities()}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
